import type { ConfiguredProductInput, PublicProduct } from "../../../../shared/types";
import type { CartLine } from "../../../contexts/CartContext";

export function configuredProductSummary(
  product: PublicProduct,
  configuration: ConfiguredProductInput,
): string[] {
  const details: string[] = [];
  const variant = product.variants.find(
    (item) => item.id === configuration.variantId,
  );
  if (variant) details.push(variant.name);
  for (const selectedGroup of configuration.groups) {
    const group = product.optionGroups.find(
      (item) => item.id === selectedGroup.groupId,
    );
    for (const selectedOption of selectedGroup.options) {
      const option = group?.options.find(
        (item) => item.id === selectedOption.optionId,
      );
      if (option) {
        const optionLabel =
          selectedOption.quantity > 1
            ? `${option.name} x${selectedOption.quantity}`
            : option.name;
        details.push(group ? `${group.name}: ${optionLabel}` : optionLabel);
      }
    }
  }
  for (const id of configuration.excludedIngredientIds) {
    const ingredient = product.ingredients.find((item) => item.id === id);
    if (ingredient) details.push(`Sin ${ingredient.name}`);
  }
  for (const id of configuration.includedIngredientIds) {
    const ingredient = product.ingredients.find((item) => item.id === id);
    if (ingredient) details.push(`Con ${ingredient.name}`);
  }
  return details;
}

export function configurationSummary(
  line: CartLine,
  products: ReadonlyMap<string, PublicProduct>,
): string[] {
  const details = configuredProductSummary(line.product, line.configuration);
  for (const component of line.configuration.comboComponents) {
    const componentDefinition = line.product.comboComponents.find(
      (item) => item.id === component.componentId,
    );
    component.units.forEach((unit, index) => {
      const componentProduct = products.get(unit.productId);
      if (!componentProduct) return;
      const configuration = configuredProductSummary(componentProduct, unit);
      const unitLabel = component.units.length > 1 ? ` ${index + 1}` : "";
      details.push(
        `${componentDefinition?.name ?? "Componente"}${unitLabel}: ${componentProduct.name}` +
          (configuration.length ? ` (${configuration.join("; ")})` : ""),
      );
    });
  }
  return details;
}

export function ingredientPayload(
  product: PublicProduct,
  configuration: ConfiguredProductInput,
) {
  const excluded = new Set(configuration.excludedIngredientIds);
  const included = new Set(configuration.includedIngredientIds);
  return product.ingredients.map((ingredient) => ({
    ingredientId: ingredient.id,
    included: ingredient.includedByDefault
      ? !excluded.has(ingredient.id)
      : included.has(ingredient.id),
  }));
}
